import type { Upgrade } from '../systems/upgrade-pool';
import { consumeReroll, canReroll } from '../systems/reroll';

/**
 * 稀有度分級 — 純遊戲機制(與修煉值/答題無關)。
 *
 * 視覺契約見 docs/spec/UPGRADE_CHOICE_DESIGN.md §3.1。
 * 給整合用的 class 命名:卡片掛 `rarity-{level}`(common/rare/epic/legendary)。
 * 色盲友善:同時用「邊框粗細 + 動態強度」當第二編碼,不只靠色相。
 */
export type Rarity = 'common' | 'rare' | 'epic' | 'legendary';

const RARITY_ORDER: Rarity[] = ['common', 'rare', 'epic', 'legendary'];

/** 中文稀有度標籤(K-3 看得懂)。 */
const RARITY_LABEL: Record<Rarity, string> = {
  common: '普通',
  rare: '稀有',
  epic: '史詩',
  legendary: '傳說',
};

/**
 * 安全取出升級的 rarity。
 *
 * 本 worktree 從 master 開,當下 `Upgrade` 介面尚未有 `rarity` 欄位
 * (由另一隻 agent 在 upgrade-pool.ts 加可選欄位)。因此這裡用 structural 讀取
 * 並對 undefined / 非法值 fallback 'common',確保本 worktree tsc 綠、
 * 合併後欄位存在時自動生效。
 */
export function resolveRarity(u: Upgrade): Rarity {
  const raw = (u as { rarity?: unknown }).rarity;
  if (typeof raw === 'string' && (RARITY_ORDER as string[]).includes(raw)) {
    return raw as Rarity;
  }
  return 'common';
}

/**
 * 場內武器進化「可進化!」標記 (R-004 包 C2)。scene 注入此 callback:對每張候選卡
 * 回傳「再選這張就會觸發的進化名」(可進化) 或 null (不會)。為 null/未傳 = 不標記,
 * 行為同今日。純視覺引導,完全不碰任何戰力/progress。
 */
export type EvolveHintFn = (u: Upgrade) => string | null;

/**
 * 重抽(reroll)設定 — ROGUELITE_ROADMAP P0「重抽消費」。傳入則三選一下方出現「🎲 重抽」鈕。
 *   - charges:本場剩餘重抽次數(scene 從 `qol.rerollCharges` 載入,封頂 2)。
 *   - getChoices:消費一次 → 回傳「重洗後的三張」(scene 端再 pick 一次同一池)。
 *   - onChargesChanged:picker 是本次計數權威,扣完透過此 callback 同步回 scene(跨升級保留)。
 * 鐵則:只重洗、不增加總拿牌數、不寫戰力 →「更多選擇」非「更容易贏」。
 */
export interface RerollConfig {
  charges: number;
  getChoices: () => Upgrade[];
  onChargesChanged?: (remaining: number) => void;
}

export function showUpgradePicker(
  choices: Upgrade[],
  onPick: (u: Upgrade) => void,
  evolveHint?: EvolveHintFn,
  reroll?: RerollConfig,
): void {
  const root = document.createElement('div');
  root.className = 'upgrade-picker';
  root.innerHTML = `
    <h2>選一個強化</h2>
    <div class="upgrade-grid"></div>
    <div class="upgrade-reroll"></div>
  `;
  const grid = root.querySelector('.upgrade-grid')!;
  const rerollBar = root.querySelector('.upgrade-reroll') as HTMLElement;
  let currentChoices = choices;
  let rerollLeft = reroll ? Math.max(0, Math.floor(reroll.charges)) : 0;
  const cards: HTMLButtonElement[] = [];
  let selectedIndex = 0;
  let closed = false;

  // 2026-06-05 老闆「PC 三選一能用鍵盤左右選、空白確定嗎?平板就用觸控」:
  // 加 keydown 導覽(←→/↑↓ 移動高亮、空白/Enter 確定),平板無鍵盤照舊點卡片。
  // close() 同時拆 listener,避免一場多次升級殘留多個 handler。
  function close(): void {
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', onKey);
    root.remove();
  }
  function highlight(): void {
    cards.forEach((c, i) => c.classList.toggle('selected', i === selectedIndex));
  }
  function choose(i: number): void {
    const u = currentChoices[i];
    if (!u) return;
    close();
    onPick(u);
  }
  function onKey(e: KeyboardEvent): void {
    if (closed || cards.length === 0) return;
    const n = cards.length;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      selectedIndex = (selectedIndex + 1) % n; highlight(); e.preventDefault();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      selectedIndex = (selectedIndex - 1 + n) % n; highlight(); e.preventDefault();
    } else if (e.key === ' ' || e.key === 'Spacebar' || e.key === 'Enter') {
      e.preventDefault(); choose(selectedIndex);
    }
  }

  // 建/重建三張卡(重抽會重畫 → 抽成函式;清空 grid + cards 後依 currentChoices 重建)。
  function renderCards(): void {
    grid.innerHTML = '';
    cards.length = 0;
    for (let idx = 0; idx < currentChoices.length; idx++) {
      const u = currentChoices[idx];
      const rarity = resolveRarity(u);
      const card = document.createElement('button');
      // rarity-{level} = 給 CSS 套四級視覺(邊框/主色/光暈/動態)的整合 class。
      card.className = `upgrade-card rarity-${rarity}`;
      card.dataset.rarity = rarity;
      // 場內武器進化「可進化!」標記 (R-004 包 C2):選這張就觸發進化時,卡上加醒目徽章
      // 引導 K-3「再選這張就進化」。evolveHint 未傳 / 回 null = 不標,行為同今日。
      const evoName = evolveHint ? evolveHint(u) : null;
      const evoBadge = evoName
        ? `<div class="up-evolve-badge" data-evolve="${evoName}">⚡可進化!</div>`
        : '';
      if (evoName) card.classList.add('can-evolve');
      // K-3 化版型(spec §2.3):稀有度標籤 → 大 emoji → 短標 displayName → 次要小字 description。
      card.innerHTML = `
        ${evoBadge}
        <div class="up-rarity">${RARITY_LABEL[rarity]}</div>
        <div class="up-emoji">${u.emoji}</div>
        <div class="up-name">${u.displayName}</div>
        <div class="up-desc">${u.description}</div>
      `;
      const here = idx;
      card.addEventListener('click', () => choose(here));
      cards.push(card);
      grid.appendChild(card);
    }
    selectedIndex = 0;
    highlight();
  }

  // 🎲 重抽鈕(ROGUELITE_ROADMAP P0):剩餘 >0 才出現。點一下消費 1 次、重洗三張、更新計數;
  // 用完灰掉。鐵則:只重洗、不增加總拿牌、不寫戰力。
  function renderReroll(): void {
    if (!reroll) return;
    rerollBar.innerHTML = '';
    const btn = document.createElement('button');
    btn.className = 'upgrade-reroll-btn';
    const enabled = canReroll(rerollLeft);
    btn.disabled = !enabled;
    if (!enabled) btn.classList.add('used-up');
    btn.textContent = enabled ? `🎲 重抽 (剩 ${rerollLeft})` : '🎲 重抽 (用完)';
    btn.addEventListener('click', () => {
      const res = consumeReroll(rerollLeft);
      if (!res.ok) return;
      rerollLeft = res.remaining;
      currentChoices = reroll.getChoices();      // scene 重 pick 同一池(只重洗,不增牌)
      reroll.onChargesChanged?.(rerollLeft);     // picker 是計數權威 → 同步回 scene(跨升級保留)
      renderCards();
      renderReroll();
    });
    rerollBar.appendChild(btn);
  }

  renderCards();
  renderReroll();

  if (cards.length > 0) {
    document.addEventListener('keydown', onKey);
    highlight();
  }
  document.body.appendChild(root);
}
